import { IOption, Palette, isToolsPage } from '../../base';
import i18next from 'i18next';
import { IColorsObject } from '../interfaces/base.interfaces';

export const isValidString = (str: string | null | undefined): boolean => {
    return str !== null && str !== undefined && str.trim() !== '';
};

export const capitalizeFirstLetter = (str: string): string => {
    if (!str) return ''; // Return empty string if input is falsy

    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const hasIconUrl = (options: Array<IOption>): boolean => {
    return options.some(
        (option) => option.iconUrl !== undefined && option.iconUrl !== ''
    );
};

export interface ScoreRange {
    value: number;
    label: string;
}

const defaultRanges: Array<ScoreRange> = [
    { value: 0, label: 'niceToHave' },
    { value: 1, label: 'niceToHave' },
    { value: 2, label: 'niceToHave' },
    { value: 3, label: 'lowPriority' },
    { value: 4, label: 'lowPriority' },
    { value: 5, label: 'moderatePriority' },
    { value: 6, label: 'moderatePriority' },
    { value: 7, label: 'highPriority' },
    { value: 8, label: 'highPriority' },
    { value: 9, label: 'essential' },
    { value: 10, label: 'essential' },
];

export const overallScoreLabels: Array<ScoreRange> = [
    { value: 0, label: 'lowerMatch' },
    { value: 1, label: 'lowerMatch' },
    { value: 2, label: 'lowerMatch' },
    { value: 3, label: 'lowMatch' },
    { value: 4, label: 'lowMatch' },
    { value: 5, label: 'averageMatch' },
    { value: 6, label: 'averageMatch' },
    { value: 7, label: 'aboveAverageMatch' },
    { value: 8, label: 'aboveAverageMatch' },
    { value: 9, label: 'excellentMatch' },
    { value: 10, label: 'excellentMatch' },
];

export const evaluatedRequirementLabels: Array<ScoreRange> = [
    { value: 0, label: 'bad' },
    { value: 1, label: 'bad' },
    { value: 2, label: 'bad' },
    { value: 3, label: 'bad' },
    { value: 4, label: 'bad' },
    { value: 5, label: 'good' },
    { value: 6, label: 'good' },
    { value: 7, label: 'veryGood' },
    { value: 8, label: 'veryGood' },
    { value: 9, label: 'excellent' },
    { value: 10, label: 'excellent' },
];

export const createScoreRangeConfig = (customRanges?: Array<ScoreRange>) => ({
    ranges: customRanges || defaultRanges,
    getLabel: (from: number, to: number): string => {
        const ranges = customRanges || defaultRanges;
        const fromLabel = ranges.find((r) => r.value === from)?.label;
        const toLabel = ranges.find((r) => r.value === to)?.label;

        if (fromLabel === toLabel) {
            return `${i18next.t(`saveJob.${fromLabel}`)}`;
        }

        return `${i18next.t(`saveJob.${fromLabel}`)}-${i18next.t(`saveJob.${toLabel}`)}`;
    },
    getSingleLabel: (to: number): string | undefined => {
        const ranges = customRanges || defaultRanges;
        const rangeLabel = ranges.find((r) => r.value === to)?.label;
        return rangeLabel ? i18next.t(`saveJob.${rangeLabel}`) : undefined;
    },
});

export const getColorByEvaluatedScore = (
    score: number | null,
    baseColors: IColorsObject
): string => {

    if (score === null) {
        return baseColors.secondary;
    } else if (score <= 4) {
        return baseColors.error; // Red for low scores
    } else if (score <= 6) {
        return baseColors.warning; // Yellow for mid-range scores
    } else {
        return baseColors.sucess; // Green for high scores
    }
};

export const getQueryParamsString = (
    queryParams: Record<string, string>
): string => {
    const queryString = new URLSearchParams(queryParams).toString();
    return queryString;
};

export const getBasicColorsObject = (palette: Palette): IColorsObject => {
    return {
        error: palette.error.main,
        warning: palette.warning.main,
        sucess: palette.success.main,
        secondary: palette.text.secondary,
        primary: palette.primary.main,
        info: palette.info.main,
        disabled: palette.text.disabled,
    };
};

export const getToolsPrefix = () => {
    return isToolsPage() ? 'tools/' : '';
};

export const a11yProps = (index: number) => {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
};

export const getHexColorWithOpacity = (hexColor: string, opacityPercent: number): string => {
    const cleanHex = hexColor.replace('#', '');
    const alpha = Math.round((opacityPercent / 100) * 255).toString(16).padStart(2, '0');
    return `#${cleanHex}${alpha}`;
};
